/**
 * §P.11 fase 4 · El tablón de segunda mano, para moderar.
 *
 * ## Solo dos rutas, y con eso basta
 *
 * Ver y retirar. El anuncio lo escribe una familia desde su App; aquí el club decide
 * si se queda. **Sin esas dos rutas no hay forma de moderarlo**, y un tablón entre
 * familias sin moderación en un club con menores no se abre.
 *
 * ## Compite con lo que vende el club, y se dice
 *
 * Un dobok de segunda mano a 20 € es un dobok nuevo de 45 € que no se vende. Por eso
 * lo activa cada club desde sus ajustes, y por eso esta pantalla dice para qué sirve
 * en vez de dar por hecho que quien la abre ya lo ha decidido.
 *
 * ## Sin fotos, y no es una limitación pendiente
 *
 * Es el único sitio donde una familia podría subir un fichero, y por eso no existe la
 * columna. Texto, talla y precio.
 *
 * ## Las denuncias, arriba y en rojo · 17-10-2026
 *
 * Desde la App, una familia puede denunciar el anuncio de otra: deja de verlo ella y
 * al dueño y a la administración les llega el aviso. **Retirarlo es cosa del club**, y
 * por eso aquí el denunciado va primero, con lo que dijeron —sin quién: se modera el
 * anuncio, no a quien avisa—.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { euros, fecha } from '@/shared/ui/formato';
import type { AnuncioDeSegundaMano } from '@/club/material/tipos';

const ESTADOS: Readonly<Record<AnuncioDeSegundaMano['status'], string>> = {
  active: 'En el tablón',
  reserved: 'Reservado',
  sold: 'Vendido',
  withdrawn: 'Retirado',
};

export function SegundaMano({
  club,
  puedeEditar,
}: {
  club: string;
  puedeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [verRetirados, setVerRetirados] = useState(false);
  const [problema, setProblema] = useState<Problema | null>(null);
  const [retirando, setRetirando] = useState<string | null>(null);

  const anuncios = useQuery({
    queryKey: ['segunda-mano', club, verRetirados],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club })['second-hand'].get({
        query: verRetirados ? { include_withdrawn: true } : {},
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as AnuncioDeSegundaMano[];
    },
  });

  const retirar = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.v1.clubs({ club })['second-hand']({ id }).delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setRetirando(null);
      setProblema(null);
      void cache.invalidateQueries({ queryKey: ['segunda-mano', club] });
    },
    onError: (e) => {
      setRetirando(null);
      setProblema(aProblema(e));
    },
  });

  if (anuncios.isPending) {
    return <Cargando filas={3} etiqueta="Cargando el tablón" />;
  }

  if (anuncios.isError || !anuncios.data) {
    return (
      <PanelError
        problema={aProblema(anuncios.error)}
        reintentar={() => void anuncios.refetch()}
      />
    );
  }

  return (
    <section>
      <header className="titulo-fila">
        <h1>Segunda mano</h1>
        <button
          type="button"
          className={verRetirados ? 'pestaña-activa' : 'btn-plano'}
          onClick={() => setVerRetirados(!verRetirados)}
        >
          {verRetirados ? 'Solo lo que está puesto' : 'Ver lo retirado'}
        </button>
      </header>

      <p className="tenue">
        Lo que ponen las familias entre ellas: el dobok que se le ha quedado
        pequeño a uno le vale al siguiente.{' '}
        <strong>Tú decides qué se queda</strong>, y el tablón entero se apaga
        desde «El club» → «Ajustes» si prefieres no tenerlo.
      </p>

      {problema && <PanelError problema={problema} />}

      {anuncios.data.length === 0 ? (
        <Vacio
          titulo="No hay ningún anuncio"
          explicacion="Las familias los ponen desde su App. Si el tablón está apagado en los ajustes del club, no verán la opción."
        />
      ) : (
        <ul className="papeles">
          {porDenuncias(anuncios.data).map((a) => (
            <li key={a.id}>
              <div className="aviso-cabeza">
                <strong>{a.title}</strong>
                <span className="pastilla pastilla-left">
                  {ESTADOS[a.status]}
                </span>
                {a.price_cents !== null && (
                  <span className="pastilla pastilla-left">
                    {euros(a.price_cents)}
                  </span>
                )}
              </div>

              {a.description && <p>{a.description}</p>}

              {a.reports.length > 0 && (
                <div className="aviso aviso-grave" role="note">
                  <strong>
                    {a.reports.length === 1
                      ? 'Una familia lo ha denunciado'
                      : `${a.reports.length} familias lo han denunciado`}
                  </strong>
                  {a.reports.map((r, i) => (
                    <p key={i}>
                      {r.reason ? `«${r.reason}»` : 'Sin motivo'} ·{' '}
                      {fecha(r.reported_at)}
                    </p>
                  ))}
                </div>
              )}

              <p className="tenue">
                {a.seller_name}
                {a.size && ` · talla ${a.size}`}
                {a.category && ` · ${a.category}`} · puesto el{' '}
                {fecha(a.created_at)}
              </p>

              {puedeEditar &&
                a.status !== 'withdrawn' &&
                (retirando === a.id ? (
                  <div className="hueco hueco-confirmar">
                    <h3>¿Retirar «{a.title}»?</h3>
                    <ul className="lista-clara">
                      <li>
                        Deja de verse en el tablón de las familias. Quien lo
                        puso no recibe ningún aviso.
                      </li>
                      <li>
                        <strong>No se borra.</strong> Queda retirado, y por eso
                        se puede comprobar después qué se quitó y cuándo.
                      </li>
                    </ul>
                    <div className="hueco-acciones">
                      <button
                        type="button"
                        className="btn-peligro"
                        disabled={retirar.isPending}
                        onClick={() => retirar.mutate(a.id)}
                      >
                        {retirar.isPending ? 'Retirando…' : 'Sí, retirarlo'}
                      </button>
                      <button
                        type="button"
                        className="btn-plano"
                        onClick={() => setRetirando(null)}
                      >
                        Dejarlo
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="acciones">
                    <button
                      type="button"
                      className="btn-plano"
                      onClick={() => setRetirando(a.id)}
                    >
                      Retirar del tablón
                    </button>
                  </div>
                ))}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Lo denunciado y aún en el tablón, primero: es lo que pide una decisión. */
function porDenuncias(
  lista: readonly AnuncioDeSegundaMano[]
): AnuncioDeSegundaMano[] {
  const pendiente = (a: AnuncioDeSegundaMano) =>
    a.reports.length > 0 && a.status !== 'withdrawn' ? 0 : 1;
  return [...lista].sort((a, b) => pendiente(a) - pendiente(b));
}
